import { LBtn } from "@components";
import { DropdownMenuOption, LDropdownMenu } from "@components/LDropdownMenu";
import {
    getScheduledForUTC,
    getToday,
    getTomorrow,
    isScheduledForSameWeek,
    stringDateCompare
} from "@helpers/dateHelpers";
import { UpcomingAssessment } from "@store/locationsSlice";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { AssessmentBox } from "../AssessmentBox";

const getOptions = (handler: (order: Order) => void): DropdownMenuOption[] => [
    {
        optionName: "Name, Alphabetic: A to Z",
        children: (
            <div>
                <p className="is-small label">Sort By: </p>
                <a
                    className="has-text-primary-blue has-text-weight-bold"
                    onClick={() => {
                        handler(Order.AZ);
                    }}
                >
                    Name, Alphabetic: A to Z
                </a>
            </div>
        )
    },
    {
        optionName: "Name, Alphabetic: Z to A",
        children: (
            <a
                className="has-text-primary-blue has-text-weight-bold"
                onClick={() => {
                    handler(Order.ZA);
                }}
            >
                Name, Alphabetic: Z to A
            </a>
        )
    },
    {
        optionName: "Date Ascending",
        children: (
            <a
                className="has-text-primary-blue has-text-weight-bold"
                onClick={() => {
                    handler(Order.DateAsc);
                }}
            >
                Date Ascending
            </a>
        )
    },
    {
        optionName: "Date Descending",
        children: (
            <a
                className="has-text-primary-blue has-text-weight-bold"
                onClick={() => {
                    handler(Order.DateDesc);
                }}
            >
                Date Descending
            </a>
        )
    }
];

enum Filters {
    Today,
    Tomorrow,
    ThisWeek,
    All
}

enum Order {
    AZ,
    ZA,
    DateAsc,
    DateDesc
}

const FilterButton = ({
    name,
    filter,
    activeFilter,
    onClick
}: {
    name: string;
    filter: Filters;
    activeFilter: Filters;
    onClick: (filter: Filters) => void;
}): JSX.Element => {
    const isActive = filter === activeFilter;

    return (
        <LBtn
            onClick={() => onClick(filter)}
            variation="outline"
            smallSpacing
            classes={`px-3 ${isActive ? "has-background-teal has-text-white is-teal" : ""}`}
        >
            {name}
        </LBtn>
    );
};

export const LeftSide = ({ assessments, handleSelectExaminee }: LeftSideProps): JSX.Element => {
    const validateMethods = useForm();

    const [activeFilter, setActiveFilter] = useState<Filters>(Filters.All);
    const [sortOrder, setSortOrder] = useState<Order>(Order.AZ);

    const handleSetFilter = (filter: Filters): void => {
        setActiveFilter(filter);
    };

    const today = getToday();
    const tomorrow = getTomorrow();

    const sortedAssessments = assessments.sort((a, b) => {
        switch (sortOrder) {
            case Order.AZ:
                return a.examineeLastName.localeCompare(b.examineeLastName);
            case Order.ZA:
                return b.examineeLastName.localeCompare(a.examineeLastName);
            case Order.DateAsc:
                return stringDateCompare(a.scheduledFor, b.scheduledFor);
            case Order.DateDesc:
                return stringDateCompare(b.scheduledFor, a.scheduledFor);
            default:
                return 0;
        }
    });

    const filteredAssessments = sortedAssessments.filter((a) => {
        const scheduled = getScheduledForUTC(a.scheduledFor);

        switch (activeFilter) {
            case Filters.All:
                return true;
            case Filters.Today:
                return today === scheduled;
            case Filters.Tomorrow:
                return tomorrow === scheduled;
            case Filters.ThisWeek:
                return isScheduledForSameWeek(scheduled);
            default:
                return false;
        }
    });

    const handleOrder = (order: Order): void => {
        setSortOrder(order);
    };

    return (
        <div className="column">
            <div className="is-flex mx-6 mb-4 is-align-items-center is-justify-content-space-between">
                <div>
                    <FilterButton
                        activeFilter={activeFilter}
                        name="Today"
                        filter={Filters.Today}
                        onClick={handleSetFilter}
                    />
                    <FilterButton
                        activeFilter={activeFilter}
                        name="Tomorrow"
                        filter={Filters.Tomorrow}
                        onClick={handleSetFilter}
                    />
                    <FilterButton
                        activeFilter={activeFilter}
                        name="This Week"
                        filter={Filters.ThisWeek}
                        onClick={handleSetFilter}
                    />
                    <FilterButton
                        activeFilter={activeFilter}
                        name="All"
                        filter={Filters.All}
                        onClick={handleSetFilter}
                    />
                </div>
                <FormProvider {...validateMethods}>
                    <LDropdownMenu
                        options={getOptions(handleOrder)}
                        id="sort"
                        type="sort"
                        hideErrorLabel
                        isSingular
                        isRightAligned
                    />
                </FormProvider>
            </div>
            <div className="mx-6">
                {filteredAssessments.map((x, idx) => {
                    return (
                        <div key={`${x.id}-${idx}`}>
                            <AssessmentBox
                                assessment={x}
                                isFirst={idx === 0}
                                isLast={idx === filteredAssessments.length - 1}
                                onSelected={() => handleSelectExaminee(x)}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

interface LeftSideProps {
    assessments: UpcomingAssessment[];
    handleSelectExaminee: (x: UpcomingAssessment) => void;
}
